import { isStaffingAgency } from '../company/is-staffing-agency';

/**
 * Which side of the Finnish labour market an employer sits on, for the role salary page's
 * "who pays what" comparison: public employers (wellbeing areas, municipalities, HUS, the
 * state), staffing agencies, and everyone else.
 *
 * There is no sector field on companies or jobs, so this is a heuristic over two signals that
 * are reliable for the public side: the employer's TOL industry ("Julkinen yleishallinto" /
 * "General public administration") and its legal name. Wellbeing areas are often missing from
 * the Companies table entirely (HUS and Varsinais-Suomen hyvinvointialue had no company record
 * on 2026-10-08), so the name has to carry them. Universities and state-owned companies are
 * deliberately left in `private`: Statistics Finland files them outside local government too,
 * and the comparison this feeds is "public care employers vs private ones".
 */
export type EmployerSector = 'public' | 'private' | 'agency';

const PUBLIC_NAME_RE = new RegExp(
  [
    'hyvinvointialue',
    'välfärdsområde',
    'kuntayhtymä',
    'samkommun',
    'sairaanhoitopiiri',
    'sjukvårdsdistrikt',
    // A municipality's own name ("Helsingin kaupunki", "Pirkkalan kunta", "Vasa stad"). The word
    // boundary after the noun keeps "Kaupunkiliikenne Oy" and "Kuntarekry" out.
    '\\bkaupunki\\b',
    '\\bkunta\\b',
    '\\bstad\\b',
    '\\bkommun\\b',
    // HUS is a wellbeing-area-level joint authority ("HUS Helsingin yliopistollinen sairaala",
    // "HUS Diagnostiikkakeskus").
    '^hus\\b',
    'yliopistollinen sairaala',
  ].join('|'),
  'i',
);

const PUBLIC_INDUSTRY_RE = /julkinen yleishallinto|general public administration|public administration|allmän offentlig förvaltning/i;

export function classifyEmployerSector(input: {
  companyId: string;
  companyName?: string | null;
  industries?: string[] | null;
}): EmployerSector {
  const name = (input.companyName ?? '').trim() || input.companyId;
  if (PUBLIC_NAME_RE.test(name) || PUBLIC_NAME_RE.test(input.companyId)) return 'public';
  if ((input.industries ?? []).some((i) => PUBLIC_INDUSTRY_RE.test(i))) return 'public';
  if (isStaffingAgency(input.companyId, name)) return 'agency';
  return 'private';
}

/**
 * Finer split of the same signals for research (data-lake-research-quality-plan.md, phase 1),
 * aligned with Statistics Finland's pay-structure sectors so advertised pay can be compared with
 * official pay sector by sector. Public bodies are absent from the PRH open API (municipalities and
 * wellbeing services counties return nothing), so the name carries the classification.
 */
export type EmployerSectorDetail =
  | 'wellbeing_area'
  | 'municipal'
  | 'joint_municipal'
  | 'state_agency'
  | 'public_other'
  | 'university'
  | 'nonprofit'
  | 'agency'
  | 'private';

const WELLBEING_RE = /hyvinvointialue|välfärdsområde|^hus\b|hus-yhtymä|sairaanhoitopiiri|sjukvårdsdistrikt|yliopistollinen sairaala/i;
const JOINT_MUNICIPAL_RE = /kuntayhtymä|samkommun/i;
const MUNICIPAL_RE = /\bkaupunki\b|\bkunta\b|\bstad\b|\bkommun\b/i;
const STATE_AGENCY_RE =
  /ministeriö|ministeriet|virasto|verket\b|verohallinto|puolustusvoimat|rajavartiolaitos|tulli\b|poliisi(?!kalusto)|maanmittauslaitos|traficom|väylävirasto|ely-keskus|aluehallintovirasto|oikeusministeriö|tuomioistuin|käräjäoikeus|syyttäjälaitos|rikosseuraamuslaitos|valtiokonttori|kansallisarkisto|museovirasto|metsähallitus|ilmatieteen laitos|tilastokeskus|^kela\b|kansaneläkelaitos/i;
const UNIVERSITY_RE = /yliopisto(?!llinen sairaala)|university|universitet|korkeakoulu|högskola/i;
const NONPROFIT_FORMS = /aatteellinen yhdistys|säätiö|uskonnollinen yhdyskunta|seurakunta|förening|stiftelse/i;

export function classifyEmployerSectorDetail(input: {
  companyId: string;
  companyName?: string | null;
  industries?: string[] | null;
  companyForm?: string | null;
  isRecruitmentCompany?: boolean | null;
}): EmployerSectorDetail {
  const name = (input.companyName ?? '').trim() || input.companyId;
  const test = (re: RegExp) => re.test(name) || re.test(input.companyId);
  if (test(WELLBEING_RE)) return 'wellbeing_area';
  if (test(JOINT_MUNICIPAL_RE)) return 'joint_municipal';
  if (test(MUNICIPAL_RE)) return 'municipal';
  if (test(STATE_AGENCY_RE)) return 'state_agency';
  if ((input.industries ?? []).some((i) => PUBLIC_INDUSTRY_RE.test(i))) return 'public_other';
  if (test(UNIVERSITY_RE)) return 'university';
  if (input.companyForm && NONPROFIT_FORMS.test(input.companyForm)) return 'nonprofit';
  if (input.isRecruitmentCompany || isStaffingAgency(input.companyId, name)) return 'agency';
  return 'private';
}

/**
 * The Statistics Finland pay-structure sector (od_statfin_pay.sector) a detail sector is compared
 * against. Universities, agencies and non-profits fall in S11_S12_S15 like companies; public_other
 * (public administration we can't place) has no single sector.
 */
export function statfinSectorFor(sector: EmployerSectorDetail): string | null {
  switch (sector) {
    case 'wellbeing_area': return 'S13132';
    case 'municipal':
    case 'joint_municipal': return 'S13131';
    case 'state_agency': return 'S13111';
    case 'public_other': return null;
    default: return 'S11_S12_S15';
  }
}
